import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricsCard from './components/MetricsCard';
import ContentQueueItem from './components/ContentQueueItem';
import FilterToolbar from './components/FilterToolbar';
import RegulatoryUpdatesPanel from './components/RegulatoryUpdatesPanel';
import BulkActionsBar from './components/BulkActionsBar';
import EmergencyAlert from './components/EmergencyAlert';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AICompliancePanel from './components/AICompliancePanel';

const ContentAnalysisDashboard = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    platform: 'all',
    riskLevel: 'all',
    status: 'all',
    dateRange: '',
    search: ''
  });
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [selectedContentForAI, setSelectedContentForAI] = useState(null);

  // Mock current user
  const currentUser = {
    name: "Sarah Johnson",
    role: "Compliance Manager"
  };

  // Mock badge counts for header
  const badgeCounts = {
    dashboard: 12,
    review: 8
  };

  // Mock emergency alert
  const [emergencyAlert, setEmergencyAlert] = useState({
    id: 'alert-001',
    contentId: 'content-789',
    platform: 'Twitter',
    message: `High-risk content detected with potential FDIC violation. Immediate review required for promotional post containing unsubstantiated deposit insurance claims.`,
    detectedAt: '2 minutes ago'
  });

  // Mock metrics data
  const metricsData = [
    {
      title: 'Violations Prevented',
      value: '247',
      change: '+12%',
      changeType: 'positive',
      icon: 'Shield',
      color: 'success'
    },
    {
      title: 'Content Processed Today',
      value: '1,834',
      change: '+8%',
      changeType: 'positive',
      icon: 'FileText',
      color: 'primary'
    },
    {
      title: 'Team Compliance Score',
      value: '94.2%',
      change: '-2%',
      changeType: 'negative',
      icon: 'TrendingUp',
      color: 'warning'
    },
    {
      title: 'Pending Reviews',
      value: '23',
      change: '+5',
      changeType: 'neutral',
      icon: 'Clock',
      color: 'error'
    }
  ];

  // Mock content queue data
  const [contentQueue] = useState([
    {
      id: 'content-001',
      platform: 'Facebook',
      preview: `New savings account with 4.5% APY! Start earning more on your deposits today. FDIC insured up to $250,000. Apply now and get $100 bonus!`,
      thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop',
      riskLevel: 'high',
      status: 'pending',
      complianceScore: 67,
      violations: 3,
      createdAt: '2 hours ago'
    },
    {
      id: 'content-002',
      platform: 'Twitter',
      preview: `Excited to announce our partnership with local businesses! Supporting community growth through financial services. #CommunityBanking`,
      thumbnail: null,
      riskLevel: 'low',
      status: 'approved',
      complianceScore: 95,
      violations: 0,
      createdAt: '4 hours ago'
    },
    {
      id: 'content-003',
      platform: 'LinkedIn',
      preview: `Investment opportunities that guarantee 15% returns annually. Our expert advisors will help you maximize your portfolio performance.`,
      thumbnail: 'https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?w=400&h=300&fit=crop',
      riskLevel: 'high',
      status: 'flagged',
      complianceScore: 23,
      violations: 8,
      createdAt: '6 hours ago'
    },
    {
      id: 'content-004',
      platform: 'Instagram',
      preview: `Thank you to our customers for trusting us with their financial goals. Here's to building stronger financial futures together! 💪`,
      thumbnail: 'https://images.pixabay.com/photo/2016/11/27/21/42/stock-1863880_960_720.jpg',
      riskLevel: 'low',
      status: 'approved',
      complianceScore: 98,
      violations: 0,
      createdAt: '8 hours ago'
    },
    {
      id: 'content-005',
      platform: 'Website',
      preview: `Personal loans starting at 2.99% APR*. Quick approval process with funds available within 24 hours. No hidden fees or prepayment penalties.`,
      thumbnail: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop',
      riskLevel: 'medium',
      status: 'pending',
      complianceScore: 78,
      violations: 2,
      createdAt: '12 hours ago'
    }
  ]);

  // Mock regulatory updates
  const regulatoryUpdates = [
    {
      id: 'update-001',
      title: 'CFPB Updates Truth in Lending Act Guidelines',
      summary: 'New requirements for APR disclosure in digital marketing materials effective January 2025.',
      details: `The Consumer Financial Protection Bureau has issued updated guidelines for Truth in Lending Act compliance in digital marketing.\n\nKey changes include:\n• Enhanced APR disclosure requirements for social media posts\n• Mandatory risk disclaimers for investment-related content\n• Updated formatting standards for mobile advertising\n\nAll marketing materials must comply with these new standards by January 15, 2025. Failure to comply may result in regulatory penalties up to $1 million per violation.`,
      severity: 'critical',
      source: 'CFPB',
      publishedAt: '2 days ago',
      affectedRules: ['TILA-001', 'TILA-045', 'CFPB-2024-12']
    },
    {
      id: 'update-002',
      title: 'FDIC Clarifies Deposit Insurance Marketing Rules',
      summary: 'Updated guidance on how financial institutions can reference FDIC insurance in promotional materials.',
      details: `The Federal Deposit Insurance Corporation has provided clarification on proper usage of FDIC insurance references in marketing materials.\n\nKey points:\n• FDIC logo usage must follow strict brand guidelines\n• Insurance coverage limits must be clearly stated\n• Misleading insurance claims are subject to immediate enforcement action\n\nThese clarifications take effect immediately and apply to all digital and print marketing materials.`,
      severity: 'important',
      source: 'FDIC',
      publishedAt: '5 days ago',
      affectedRules: ['FDIC-300', 'FDIC-301']
    },
    {
      id: 'update-003',
      title: 'SEC Social Media Guidance for Financial Advisors',
      summary: 'New social media compliance requirements for investment advisors and broker-dealers.',
      details: `The Securities and Exchange Commission has issued comprehensive guidance for social media usage by financial advisors.\n\nNew requirements include:\n• Pre-approval workflows for all investment-related social media content\n• Enhanced record-keeping for social media communications\n• Specific disclosure requirements for testimonials and endorsements\n\nCompliance deadline is March 1, 2025.`,
      severity: 'informational',
      source: 'SEC',
      publishedAt: '1 week ago',
      affectedRules: ['SEC-2024-08', 'IA-2024-15']
    }
  ];

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Sofron.ai', href: '/content-analysis-dashboard' },
    { label: 'Content Analysis Dashboard' }
  ];

  // Filter content based on current filters
  const filteredContent = contentQueue?.filter(item => {
    if (filters?.platform !== 'all' && item?.platform?.toLowerCase() !== filters?.platform) return false;
    if (filters?.riskLevel !== 'all' && item?.riskLevel !== filters?.riskLevel) return false;
    if (filters?.status !== 'all' && item?.status !== filters?.status) return false;
    if (filters?.search && !item?.preview?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    return true;
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      platform: 'all',
      riskLevel: 'all',
      status: 'all',
      dateRange: '',
      search: ''
    });
  };

  const handleContentReview = (contentId) => {
    navigate('/content-review-detail', { state: { contentId } });
  };

  const handleContentApprove = (contentId) => {
    console.log('Approving content:', contentId);
    // Implementation would update content status
  };

  const handleContentFlag = (contentId) => {
    console.log('Flagging content:', contentId);
    // Implementation would update content status
  };

  const handleBulkApprove = () => {
    console.log('Bulk approving:', selectedItems);
    setSelectedItems([]);
  };

  const handleBulkFlag = () => {
    console.log('Bulk flagging:', selectedItems);
    setSelectedItems([]);
  };

  const handleBulkDelete = () => {
    console.log('Bulk deleting:', selectedItems);
    setSelectedItems([]);
  };

  const handleSelectAll = () => {
    if (selectedItems?.length === filteredContent?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredContent?.map(item => item?.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleEmergencyTakedown = (contentId) => {
    console.log('Emergency takedown for:', contentId);
    setEmergencyAlert(null);
  };

  const handleDismissAlert = (alertId) => {
    setEmergencyAlert(null);
  };

  const handleAIAnalysisComplete = (analysisResult) => {
    console.log('AI Analysis completed:', analysisResult);
    // Here you could update the content item with real AI analysis results
    // instead of using mock data
  };

  const handleAISuggestionApply = (suggestion) => {
    console.log('AI Suggestion applied:', suggestion);
    setShowAIPanel(false);
  };

  const handleOpenAIAnalysis = (content) => {
    setSelectedContentForAI(content);
    setShowAIPanel(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header badgeCounts={badgeCounts} currentUser={currentUser} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbItems} className="mb-6" />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">
                Content Analysis Dashboard
              </h1>
              <p className="text-muted-foreground">
                Monitor compliance status and manage content review workflows with AI-powered analysis
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                iconName="Brain"
                iconSize={16}
                onClick={() => setShowAIPanel(!showAIPanel)}
              >
                AI Assistant
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                iconSize={16}
              >
                Export Report
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconSize={16}
                onClick={() => navigate('/content-review-detail')}
              >
                New Review
              </Button>
            </div>
          </div>

          {/* Emergency Alert */}
          <EmergencyAlert
            alert={emergencyAlert}
            onDismiss={handleDismissAlert}
            onTakedown={handleEmergencyTakedown}
          />

          <div className={`grid gap-8 ${showAIPanel ? 'grid-cols-1 xl:grid-cols-5' : 'grid-cols-1 xl:grid-cols-4'}`}>
            {/* Main Content Area */}
            <div className={`space-y-6 ${showAIPanel ? 'xl:col-span-3' : 'xl:col-span-3'}`}>
              {/* Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {metricsData?.map((metric, index) => (
                  <MetricsCard
                    key={index}
                    title={metric?.title}
                    value={metric?.value}
                    change={metric?.change}
                    changeType={metric?.changeType}
                    icon={metric?.icon}
                    color={metric?.color}
                  />
                ))}
              </div>

              {/* Filter Toolbar */}
              <FilterToolbar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                resultCount={filteredContent?.length}
              />

              {/* Bulk Actions */}
              <BulkActionsBar
                selectedCount={selectedItems?.length}
                totalCount={filteredContent?.length}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                onBulkApprove={handleBulkApprove}
                onBulkFlag={handleBulkFlag}
                onBulkDelete={handleBulkDelete}
                isAllSelected={selectedItems?.length === filteredContent?.length && filteredContent?.length > 0}
              />

              {/* Content Queue */}
              <div className="bg-card border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">Content Review Queue</h2>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="RefreshCw" size={16} />
                        <span>Last updated: 2 minutes ago</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="divide-y divide-border">
                  {filteredContent?.length === 0 ? (
                    <div className="p-12 text-center">
                      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No content found</h3>
                      <p className="text-muted-foreground">
                        Try adjusting your filters or search criteria
                      </p>
                    </div>
                  ) : (
                    filteredContent?.map((content) => (
                      <div key={content?.id} className="p-4">
                        <ContentQueueItem
                          content={content}
                          onReview={handleContentReview}
                          onApprove={handleContentApprove}
                          onFlag={handleContentFlag}
                          onAIAnalysis={() => handleOpenAIAnalysis(content)}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* AI Panel */}
            {showAIPanel && (
              <div className="xl:col-span-1">
                <AICompliancePanel
                  selectedContent={selectedContentForAI}
                  onAnalysisComplete={handleAIAnalysisComplete}
                  onSuggestionApply={handleAISuggestionApply}
                />
              </div>
            )}

            {/* Right Sidebar */}
            <div className="xl:col-span-1">
              <RegulatoryUpdatesPanel updates={regulatoryUpdates} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContentAnalysisDashboard;